/**
 * MSI3 faculty grant — client read of msi3FacultyGrants/{uid}.
 * Grants are written server-side (Phase 6 invites). A global admin is treated
 * as faculty for every org. Used only for the faculty route gate; callables
 * re-check the grant on the server.
 *
 * Landing (msi3 host) keys off hasGrant (an approved grant with orgId), not
 * isFaculty — global admins without a grant land on AcuityDrill.
 * A missing status is not approval. Same rule as
 * functions/src/msi3Teaching/facultyGrantStatus.js.
 */
import { useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";

export const MSI3_FACULTY_GRANTS_COLLECTION = "msi3FacultyGrants";

function facultyGrantIsApproved(grant) {
  return String(grant?.status || "").trim() === "approved";
}

/**
 * @param {{ enabled?: boolean }} [options]
 * @returns {{
 *   loading: boolean,
 *   isFaculty: boolean,
 *   hasGrant: boolean,
 *   isAdmin: boolean,
 *   orgId: string | null,
 *   grantStatus: string | null,
 * }}
 */
export function useMsi3FacultyGrant({ enabled = true } = {}) {
  const { user, userLoading, isGlobalAdmin } = useAuth();
  const [grant, setGrant] = useState(null);
  const [grantLoading, setGrantLoading] = useState(true);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;

  useEffect(() => {
    if (!enabled) {
      setGrant(null);
      setGrantLoading(false);
      return undefined;
    }
    if (userLoading) {
      setGrantLoading(true);
      return undefined;
    }
    if (!isRealAccount) {
      setGrant(null);
      setGrantLoading(false);
      return undefined;
    }

    let cancelled = false;
    setGrantLoading(true);
    db.collection(MSI3_FACULTY_GRANTS_COLLECTION)
      .doc(uid)
      .get()
      .then((snap) => {
        if (cancelled) return;
        setGrant(snap.exists ? snap.data() : null);
        setGrantLoading(false);
      })
      .catch((err) => {
        console.warn("[useMsi3FacultyGrant] read failed", err);
        if (cancelled) return;
        setGrant(null);
        setGrantLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, isRealAccount, userLoading, enabled]);

  const loading = Boolean(
    enabled && (userLoading || (isRealAccount && grantLoading)),
  );
  const grantOrgId =
    grant && typeof grant.orgId === "string" && grant.orgId.trim()
      ? grant.orgId.trim()
      : null;
  const grantStatus =
    grant && typeof grant.status === "string" && grant.status.trim()
      ? grant.status.trim()
      : null;
  const hasGrant = Boolean(grantOrgId) && facultyGrantIsApproved(grant);
  const isFaculty = Boolean(isGlobalAdmin || hasGrant);

  return {
    loading,
    isFaculty: loading ? false : isFaculty,
    hasGrant: loading ? false : hasGrant,
    isAdmin: Boolean(isGlobalAdmin),
    orgId: loading ? null : grantOrgId,
    grantStatus: loading ? null : grantStatus,
  };
}

export default useMsi3FacultyGrant;
